import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import {
  GetPartnersFilterModel,
  PartnerDTOPagedList,
} from "src/app/api/api-types";
import { ApiUrl } from "src/app/api/api-url-helper";
import { RequestService } from "src/app/core/request.service";
import {
  deserializePartner,
  PartnerWithSites,
} from "src/app/partner/services/partners/partner.model";
import {
  firstExistentValueFrom,
  getDefaultUpdater,
  isInstanceOf,
} from "src/utils";

@Injectable({ providedIn: "root" })
export class PartnersService {
  public constructor(private readonly request: RequestService) {}

  readonly #selectedKey = new BehaviorSubject<PartnerWithSites["key"] | null>(
    null,
  );
  public readonly selectedKeyChanges = this.#selectedKey.asObservable();
  private readonly updatesNotifier = new BehaviorSubject<void | null>(null);
  private readonly updatesNotifierChanges = this.updatesNotifier.asObservable();
  public readonly changes: Observable<readonly PartnerWithSites[]> =
    this.updatesNotifierChanges.pipe(
      switchMap(() =>
        this.request.get({
          path: ApiUrl.PARTNERS_WITH_SITES,
        }),
      ),
      map((partners) => partners.map((partner) => deserializePartner(partner))),
      shareReplay(1),
    );

  private readonly maybeSelectedChanges = combineLatest([
    this.selectedKeyChanges,
    this.changes,
  ]).pipe(
    map(
      ([key, partners]) =>
        (key && partners.find((partner) => partner.key.isSame(key))) ||
        "not-found",
    ),
    shareReplay(1),
  );

  public readonly selectedChanges = this.maybeSelectedChanges.pipe(
    filter(isInstanceOf(PartnerWithSites)),
    distinctUntilChanged(),
  );

  public readonly isSelectedLoadedChanges = this.maybeSelectedChanges.pipe(
    map((partner) => partner !== "not-found"),
  );

  private readonly defaultSelectionChanges = this.changes.pipe(
    map((partners) => (partners[0] ?? null) as PartnerWithSites | null),
  );

  private isSelectedLoadedNotifier?: Observable<boolean>;

  private readonly unloadNotifier = new Subject<void>();

  public getPaginatedListChanges(
    body: GetPartnersFilterModel,
  ): Observable<PartnerDTOPagedList> {
    return this.request.post({
      path: ApiUrl.ALL_PARTNERS,
      body,
    });
  }

  public async load(): Promise<boolean> {
    if (!this.isSelectedLoadedNotifier) {
      // Load the cache first so the default selection doesn't get applied on
      // initial load, and only runs on subsequent changes.
      await firstExistentValueFrom(this.changes);

      const [updater, notifier] = getDefaultUpdater(
        this.maybeSelectedChanges,
        this.defaultSelectionChanges,
      );
      this.isSelectedLoadedNotifier = notifier;

      // Set the default partner (for global/admin users only)
      // if one isn't selected or the selected wasn't found.
      updater
        .pipe(
          switchMap((partner) => this.setSelected(partner.key)),
          takeUntil(this.unloadNotifier),
        )
        .subscribe();
    }

    return firstValueFrom(this.isSelectedLoadedNotifier);
  }

  public notifyUpdates(): void {
    this.updatesNotifier.next();
  }

  public async setSelected(partnerKey: PartnerWithSites["key"]): Promise<void> {
    this.#selectedKey.next(partnerKey);
    await firstValueFrom(
      this.selectedChanges.pipe(
        filter((partner) => partner.key.isSame(partnerKey)),
      ),
    );
  }
}
